import { Link } from "react-router-dom";
import type { NavItem } from "@/lib/navDefaults";

interface NavMenuLinkProps {
  item: NavItem;
  className?: string;
  onAction?: (actionKey: string) => void;
  onNavigate?: () => void;
  /**
   * When provided, internal route/hash links call this instead of navigating
   * directly — lets the header keep its smooth-scroll behaviour.
   */
  onInternalNavigate?: (href: string) => void;
  children?: React.ReactNode;
}

/**
 * Renders a single navigation item across all link types so the header and
 * footer never duplicate link-resolution logic.
 */
export function NavMenuLink({
  item, className, onAction, onNavigate, onInternalNavigate, children,
}: NavMenuLinkProps) {
  const label = children ?? item.label;
  const href = item.href ?? "";

  if (item.link_type === "action") {
    return (
      <button
        type="button"
        className={className}
        onClick={() => {
          if (item.action_key) onAction?.(item.action_key);
          onNavigate?.();
        }}
      >
        {label}
      </button>
    );
  }

  if (item.link_type === "none") {
    return <span className={className}>{label}</span>;
  }


  const isExternal = item.link_type === "external" || /^https?:\/\//.test(href);
  const opensNewTab = item.open_in_new_tab || (isExternal && !href.startsWith("/"));

  if (!isExternal && !opensNewTab && onInternalNavigate) {
    return (
      <button
        type="button"
        className={className}
        onClick={() => {
          onInternalNavigate(href || "/");
          onNavigate?.();
        }}
      >
        {label}
      </button>
    );
  }

  if (isExternal || item.link_type === "hash" || opensNewTab) {
    return (
      <a
        href={href}
        className={className}
        onClick={onNavigate}
        {...(opensNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {label}
      </a>
    );
  }


  return (
    <Link to={href || "/"} className={className} onClick={onNavigate}>
      {label}
    </Link>
  );
}
